/**
 * React Query hooks for the NDT AI digests (batch 6, fieldiq-api#1177 / #1178).
 * Keys start ['ndtInspection'] like every NDT hook; reads spread
 * LOAD_FAILURE_QUERY_OPTIONS (a 403 is an answer, not a flake). A run, an
 * approve and a dismiss each invalidate the digest keys. (The AI Agent screen's
 * review list polls on its own; it holds no React Query key to invalidate.)
 *
 * Approve and dismiss go through the SHARED doors (api.agentRuns), the ones the
 * AI Agent screen's review list uses — there is no NDT-specific approve.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import type { NdtAiDigest, NdtAiRunResult, NdtAiWindow } from '../types/ndt-ai';

/** `{ data }` → data, or null. Never the envelope. */
function unwrap<T>(body: unknown): T | null {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return null;
  const d = (body as { data?: unknown }).data;
  return d === undefined || d === null ? null : (d as T);
}

export const ndtAiKeys = {
  all: ['ndtInspection', 'ai'] as const,
  digests: (agent?: string) => ['ndtInspection', 'ai', 'digests', agent ?? ''] as const,
  digest: (id: string) => ['ndtInspection', 'ai', 'digest', id] as const,
};

function invalidateAi(qc: QueryClient) {
  return qc.invalidateQueries({ queryKey: ndtAiKeys.all });
}

export function useNdtAiDigests(agent?: string, enabled = true) {
  return useQuery({
    queryKey: ndtAiKeys.digests(agent),
    queryFn: async () => {
      const d = unwrap<NdtAiDigest[]>((await api.ndtInspection.ai.digests(agent ? { agent } : undefined)).data);
      return Array.isArray(d) ? d : [];
    },
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useNdtAiDigest(id: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ndtAiKeys.digest(id ?? ''),
    queryFn: async () => unwrap<NdtAiDigest>((await api.ndtInspection.ai.digest(id as string)).data),
    enabled: enabled && !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export type NdtAiRunRequest =
  | { agent: 'ndt_repair_rate_digest'; window?: NdtAiWindow }
  | { agent: 'ndt_cert_expiry_digest' }
  | { agent: 'ndt_dose_review'; window?: NdtAiWindow }
  | { agent: 'ndt_ticket_preflight'; ticketId: string };

export function useRunNdtAi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: NdtAiRunRequest): Promise<NdtAiRunResult | null> => {
      const res =
        req.agent === 'ndt_repair_rate_digest'
          ? await api.ndtInspection.ai.repairRateDigest(req.window ?? {})
          : req.agent === 'ndt_cert_expiry_digest'
            ? await api.ndtInspection.ai.certExpiryDigest()
            : req.agent === 'ndt_dose_review'
              ? await api.ndtInspection.ai.doseReview(req.window ?? {})
              : await api.ndtInspection.ai.ticketPreflight(req.ticketId);
      return unwrap<NdtAiRunResult>(res.data);
    },
    onSuccess: () => invalidateAi(qc),
  });
}

export function useReviewNdtAi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'approve' | 'dismiss' }) => {
      if (action === 'approve') await api.agentRuns.approve(id);
      else await api.agentRuns.dismiss(id);
    },
    onSettled: () => invalidateAi(qc),
  });
}
